import { Data, Deferred, Effect, Exit, Layer, Option, Predicate, Schema } from 'effect';

import type { SlovakPostWidgetSource } from '../../config/slovakPost';
import { createScript, loadOf } from '../shipping/domAsset';
import {
  SLOVAK_POST_DEFAULT_BRANCH_KINDS,
  SLOVAK_POST_HANDSHAKE_TOKEN,
  SlovakPostEnvelopeSchema,
  SlovakPostSelectionSchema,
  SlovakPostWidget,
  SlovakPostWidgetCancelledError,
  SlovakPostWidgetNotLoadedError,
  SlovakPostWidgetPayloadError,
  SlovakPostWidgetScriptLoadError,
  toSlovakPostT,
} from './SlovakPostWidget';
import type {
  SlovakPostPickupWidget,
  SlovakPostT,
  SlovakPostWidgetError,
  SlovakPostWidgetOptions,
} from './SlovakPostWidget';

const SCRIPT_MARKER = 'data-slovak-post-widget';

const decodeEnvelope = Schema.decodeUnknownOption(Schema.fromJsonString(SlovakPostEnvelopeSchema));
const decodeSelection = Schema.decodeUnknownExit(Schema.fromJsonString(SlovakPostSelectionSchema));

const isPickupWidget = (value: unknown): value is SlovakPostPickupWidget =>
  Predicate.hasProperty(value, 'open') &&
  Predicate.isFunction(value.open) &&
  Predicate.hasProperty(value, 'close') &&
  Predicate.isFunction(value.close);

const carrierWindow = (): Window | undefined =>
  typeof window === 'undefined' ? undefined : window;

/** Narrows `globalThis.slposta.PickupWidget`; absent until the carrier script has run. */
const widgetApi = (): SlovakPostPickupWidget | undefined => {
  const carrier: unknown = Reflect.get(globalThis, 'slposta');

  if (!Predicate.hasProperty(carrier, 'PickupWidget')) {
    return undefined;
  }

  return isPickupWidget(carrier.PickupWidget) ? carrier.PickupWidget : undefined;
};

const closeWidget = Effect.sync(() => {
  widgetApi()?.close();
});

/**
 * Injects the carrier's script, once per document.
 *
 * The global being present already is the common case: the script stays loaded
 * for the life of the page, so reopening the map costs nothing.
 */
const loadWidgetScript = (scriptUrl: string) =>
  Effect.tryPromise({
    try: async () => {
      if (carrierWindow() === undefined) {
        throw new Error('Window is not available (SSR context)');
      }

      if (widgetApi() !== undefined) {
        return;
      }

      const existing = document.querySelector(`script[${SCRIPT_MARKER}]`);
      if (existing !== null) {
        return loadOf(existing, 'the Slovak Post widget script');
      }

      const script = createScript(scriptUrl, { [SCRIPT_MARKER]: '' });
      const loaded = loadOf(script, 'the Slovak Post widget script');
      document.body.appendChild(script);
      return loaded;
    },
    catch: (error) =>
      new SlovakPostWidgetScriptLoadError({
        message: 'Failed to load the Slovak Post widget script',
        cause: error,
      }),
  });

/**
 * One frame from the page's `message` stream, classified.
 *
 * `Ignore` covers everything that is not this widget answering: another
 * script's message, another merchant's token, a string that is not JSON.
 */
/* eslint-disable @typescript-eslint/no-empty-object-type */
type Frame = Data.TaggedEnum<{
  Ignore: {};
  Selected: { readonly point: SlovakPostT };
  Cancelled: {};
  Malformed: { readonly cause: unknown };
}>;
/* eslint-enable @typescript-eslint/no-empty-object-type */

const Frame = Data.taggedEnum<Frame>();

const classify = (data: unknown, token: string): Frame => {
  if (typeof data !== 'string') {
    return Frame.Ignore();
  }

  return Option.match(decodeEnvelope(data), {
    onNone: () => Frame.Ignore(),
    onSome: (envelope): Frame => {
      if (envelope.token !== token) {
        return Frame.Ignore();
      }

      if (envelope.op === 'close') {
        return Frame.Cancelled();
      }

      return Exit.match(decodeSelection(data), {
        onFailure: (cause) => Frame.Malformed({ cause }),
        onSuccess: (selection) => Frame.Selected({ point: toSlovakPostT(selection) }),
      });
    },
  });
};

/**
 * Opens the map and waits for the shopper's choice.
 *
 * The acquire attaches the `message` listener and opens the carrier's map;
 * the release detaches and closes on every exit — a resolved pick, a failed
 * one, and an interrupted one alike. Without that, every opened map would
 * leave a listener on the page for the rest of the session.
 */
const openWidget = (
  options: SlovakPostWidgetOptions
): Effect.Effect<SlovakPostT, SlovakPostWidgetError> =>
  Effect.scoped(
    Effect.gen(function* () {
      const target = carrierWindow();
      const api = widgetApi();

      if (target === undefined || api === undefined) {
        return yield* new SlovakPostWidgetNotLoadedError({
          message: 'The Slovak Post widget script is not loaded',
        });
      }

      const selection = yield* Deferred.make<
        SlovakPostT,
        SlovakPostWidgetCancelledError | SlovakPostWidgetPayloadError
      >();

      const handleMessage = (event: MessageEvent) => {
        Frame.$match(classify(event.data, SLOVAK_POST_HANDSHAKE_TOKEN), {
          Ignore: () => undefined,
          Cancelled: () => {
            Deferred.doneUnsafe(
              selection,
              Effect.fail(
                new SlovakPostWidgetCancelledError({
                  message: 'The shopper closed the Slovak Post widget',
                })
              )
            );
          },
          Malformed: (frame) => {
            Deferred.doneUnsafe(
              selection,
              Effect.fail(
                new SlovakPostWidgetPayloadError({
                  message: 'The Slovak Post widget sent a branch this client cannot read',
                  cause: frame.cause,
                })
              )
            );
          },
          Selected: (frame) => {
            Deferred.doneUnsafe(selection, Effect.succeed(frame.point));
          },
        });
      };

      yield* Effect.acquireRelease(
        Effect.sync(() => {
          target.addEventListener('message', handleMessage);
          api.open({
            embedElement: options.embedSelector,
            branchKinds: options.branchKinds ?? SLOVAK_POST_DEFAULT_BRANCH_KINDS,
            uiHideCloseButton: false,
          });
        }),
        () =>
          Effect.sync(() => {
            target.removeEventListener('message', handleMessage);
            widgetApi()?.close();
          })
      );

      return yield* Deferred.await(selection);
    })
  );

const make = Effect.succeed({
  pick: (source: SlovakPostWidgetSource, options: SlovakPostWidgetOptions) =>
    Effect.gen(function* () {
      yield* loadWidgetScript(source.scriptUrl);
      return yield* openWidget(options);
    }),

  close: closeWidget,
});

export const SlovakPostWidgetLive = Layer.effect(SlovakPostWidget, make);
